import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMap } from 'react-map-gl';
import { appStore } from 'components/map';
import { addPinAtom, locationSearchAtom, openPinIdAtom } from './atoms';
import {
  formatDecimalPair,
  parseCoordinates,
  type LngLat,
  type LocationSuggestion,
} from './coordinate-utils';

/** Close enough to read pads and roads. A closer view is never zoomed back out. */
const PIN_ZOOM = 14;

/**
 * Writes to the clipboard. The browser has none on an insecure origin, and refuses
 * one without permission; both reject.
 *
 * @param {string} text - What to copy.
 * @return {Promise<void>} Resolves once the text is on the clipboard.
 */
const writeClipboard = (text: string): Promise<void> =>
  navigator.clipboard
    ? navigator.clipboard.writeText(text)
    : Promise.reject(new Error('No clipboard'));

/**
 * Copies a spot in Google's "lat, lng" form, ready to paste into its search box.
 *
 * @param {LngLat} lngLat - The spot, `[lng, lat]`.
 * @return {Promise<void>} The clipboard write.
 */
export const copyCoordinates = (lngLat: LngLat) =>
  writeClipboard(formatDecimalPair(lngLat));

/**
 * Copies several spots, one "lat, lng" per line, so they paste as rows.
 *
 * @param {LngLat[]} spots - The spots, each `[lng, lat]`.
 * @return {Promise<void>} The clipboard write.
 */
export const copyAllCoordinates = (spots: LngLat[]) =>
  writeClipboard(spots.map(formatDecimalPair).join('\n'));

export type CopyResult = 'copied' | 'failed';

/**
 * Runs a copy and reports how it went: a short-lived "Copied" flag on success, and
 * the result for the caller's toast either way.
 *
 * @return {object} `copied`, `result` for the toast, `copy` to run a write, and
 *   `clearResult` for the toast's close.
 */
export const useCopied = () => {
  const [copied, setCopied] = useState(false);
  const [result, setResult] = useState<CopyResult | null>(null);
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = useCallback(
    (write: () => Promise<void>) =>
      write().then(
        () => {
          setResult('copied');
          setCopied(true);
          window.clearTimeout(timer.current);
          timer.current = window.setTimeout(() => setCopied(false), 1500);
          return true;
        },
        () => {
          setResult('failed');
          return false;
        }
      ),
    []
  );
  const clearResult = useCallback(() => setResult(null), []);
  return { copied, result, copy, clearResult };
};

const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_TEXT = 2;
const NO_SUGGESTIONS: LocationSuggestion[] = [];

interface SearchResult {
  suggestions: LocationSuggestion[];
  /** The search couldn't run, so no suggestions doesn't mean no matches. */
  failed: boolean;
}

const NO_RESULT: SearchResult = { suggestions: NO_SUGGESTIONS, failed: false };

/**
 * Wells, places and survey areas matching the field, once typing pauses. Idle while the
 * field holds coordinates, so a pasted pair never searches, and without the app shell's
 * search. A failed search suggests nothing, and says so. Call from inside `<Map>`; places
 * rank by its centre.
 *
 * @param {string | null} text - The field's edit, or null while it follows the cursor.
 * @return {object} `suggestions`, whether a search is `loading`, whether it `failed`, and
 *   `searchNow` for Enter pressed before the results are in.
 */
export const useLocationSuggestions = (text: string | null) => {
  const { current: map } = useMap();
  const search = useAtomValue(locationSearchAtom, appStore)?.search;
  const query = text?.trim() ?? '';
  const searchable =
    !!search && query.length >= MIN_SEARCH_TEXT && !parseCoordinates(query).ok;
  const [result, setResult] = useState<
    ({ query: string } & SearchResult) | null
  >(null);
  const enterSearch = useRef<AbortController>();
  useEffect(() => () => enterSearch.current?.abort(), []);

  const run = useCallback(
    (signal: AbortSignal): Promise<SearchResult> => {
      if (!search) return Promise.resolve(NO_RESULT);
      const center = map?.getCenter();
      return search(
        query,
        center ? [center.lng, center.lat] : undefined,
        signal
      ).then(
        (suggestions) => ({ suggestions, failed: false }),
        () => ({ suggestions: NO_SUGGESTIONS, failed: true })
      );
    },
    [map, query, search]
  );

  useEffect(() => {
    if (!searchable) return undefined;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void run(controller.signal).then((found) => {
        if (!controller.signal.aborted) setResult({ query, ...found });
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, run, searchable]);

  const settled = searchable && result?.query === query;
  const current: SearchResult = settled && result ? result : NO_RESULT;
  const searchNow = useCallback((): Promise<SearchResult> => {
    if (settled || !searchable) return Promise.resolve(current);
    enterSearch.current?.abort();
    enterSearch.current = new AbortController();
    return run(enterSearch.current.signal);
  }, [run, searchable, settled, current]);
  return {
    suggestions: current.suggestions,
    failed: current.failed,
    loading: searchable && !settled,
    searchNow,
  };
};

/**
 * Dropping a pin and flying to one. Call from inside `<Map>`.
 *
 * @return {object} `dropPin` and `flyTo`.
 */
export const usePinActions = () => {
  const { current: map } = useMap();
  const addPin = useSetAtom(addPinAtom, appStore);
  const setOpenPinId = useSetAtom(openPinIdAtom, appStore);

  const flyTo = useCallback(
    (lngLat: LngLat) => {
      if (!map) return;
      map.flyTo({
        center: lngLat,
        zoom: Math.max(map.getZoom(), PIN_ZOOM),
        duration: 1400,
      });
    },
    [map]
  );

  /**
   * `fly` is for a pin placed from somewhere other than the map — typed or pasted
   * coordinates — so the spot comes into view with its popup open. A pin placed by
   * pointing at the map is already in view.
   */
  const dropPin = useCallback(
    (lngLat: LngLat, { fly, label }: { fly: boolean; label?: string }) => {
      const id = addPin(lngLat, label);
      if (fly) {
        flyTo(lngLat);
        setOpenPinId(id);
      }
    },
    [addPin, flyTo, setOpenPinId]
  );

  return { dropPin, flyTo };
};
